import React, { useMemo } from "react";
import { Order } from "src/shared/services/interfacev2";
import { splitDateTime } from "src/shared/components/dateTime";
import { useFormatAmount } from "src/business-types/laundry/modules/home/hooks/useFormatAmount";
import { useDateTimeFormatter } from "src/shared/configurations/hooks/useDateTimeFormatter";
import { useUser } from "src/shared/context/UserContext";
import { User, Info } from "lucide-react";
import { formatNumber } from "src/business-types/laundry/modules/orders/OrderSearch";

interface OrderSummaryProps {
  order: Order;
}

const RowItem: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div className="flex justify-between px-2.5 my-2">
    <h2>{label}</h2>
    <h2 className="font-semibold">{value}</h2>
  </div>
);

const OrderSummary: React.FC<OrderSummaryProps> = ({ order }) => {
  const formatAmount = useFormatAmount();
  const { formatDate, formatTime } = useDateTimeFormatter();
  const { userConfigration } = useUser();

  const toBoolean = (value: any) => value === true || value === "true";
  const billNoEnabled = toBoolean(userConfigration?.billNoEnabled);
  const orderIdEnabled = toBoolean(userConfigration?.orderIdEnabled);
  const deliveryFeeEnabled = toBoolean(userConfigration?.deliveryFeeEnabled);

  const receivedAmount = useMemo(() => {
    if (!order?.orders?.length) return 0;

    const received = order.orders.reduce((sum, o) => sum + Number(o.receivedAmount || 0) + Number(o.ledgerReceivedAmount || 0), 0);
    const finalAmount = order.orders.reduce((sum, o) => sum + Number(o.finalAmount || 0), 0);

    return Math.min(received, finalAmount);
  }, [order]);

      const ledgerDiscount = useMemo(() => {
    if (!order?.orders?.length) return 0;
    return Number(
      order.orders.reduce((sum, order) => {
        return sum + Number(order.ledgerDiscountAmount || 0);
      }, 0),
    );
  }, [order]);

  const AdvanceAmount = order?.orders[0].creditAdjust
  const showAdvanceAmount = AdvanceAmount !== undefined && AdvanceAmount !== null && AdvanceAmount !== 0;
  const showLedgerDiscount = ledgerDiscount !== undefined &&  ledgerDiscount !== null && ledgerDiscount !== 0;

  const remainingAmount = order?.orders?.length ? Math.max(Number(order.orders.at(-1)?.remainingAmount ?? 0), 0) - (AdvanceAmount || 0): 0;
  const { date: orderDate, time: orderTime } = splitDateTime(order?.orderDetails?.[0]?.orderedOn?.$date ?? "");

  const customerName = [order?.customerDetails?.firstName, order?.customerDetails?.middleName, order?.customerDetails?.lastName].filter(Boolean).join(" ") || "Walk-in Customer";

  const firstOrder = order?.orders?.[0];
  const subtotal = (Number(firstOrder?.orderAmount.toFixed(2)));
  const discount = firstOrder?.discount || 0;
  const totalAmount = firstOrder?.finalAmount || "0";
  const deliveryFee = firstOrder?.deliveryFee || 0;
  const paymentMethod = order?.paymentHistory?.[0]?.paymentMethod || "N/A";
  const staff = firstOrder?.staffDetails;
  const staffName = [staff?.firstName, staff?.middleName, staff?.lastName].filter(Boolean).join(" ") || "N/A";

  const orderId = firstOrder?.orderId || "N/A";
  const billNumber = order?.orders?.[order.orders?.length - 1]?.billNumber;
  const outstandingAmount = order?.totalOutstandingBalance || 0;

  // Calculate net amount (total - discount)
  const netAmount = Number(totalAmount);
  const displayTotal = netAmount > 0 ? netAmount : Number(totalAmount);

  return (
    <div className="flex flex-col overflow-hidden">
      <div className="flex flex-col gap-2">
        {/* ✅ Customer Info Section */}
        <div className="bg-white border border-slate-200 rounded-xl p-1 transition-all shadow-md">
          {/* Customer Identity Section */}
          <div className="flex items-center gap-4 mb-3 pb-2 border-b border-slate-100">
            <div className="flex-shrink-0 w-10 h-10 bg-slate-900 rounded-2xl flex items-center justify-center text-white shadow-lg shadow-slate-200">
              <User className="w-5 h-5" />
            </div>
            <div className="flex flex-col">
              <span className="text-[10px] font-black uppercase tracking-[0.15em] text-slate-400">Customer Name</span>
              <h2 className="text-xl font-black text-slate-800 tracking-tight leading-tight">{customerName}</h2>
            </div>
          </div>

          {/* Staff Info */}
          <div className="flex items-center gap-3 px-2 py-1.5 bg-slate-50 border border-slate-200 rounded-xl mb-1 -mt-2">
            <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">Staff:</span>
            <span className="text-sm font-semibold text-slate-800">{staffName}</span>
          </div>

          {/* Metadata Row (Order ID & Bill No) */}
          {(orderIdEnabled || billNoEnabled) && (
            <div className="flex items-center justify-between px-2 py-1.5 bg-slate-50 border border-slate-200 rounded-xl group shadow-sm">
              {/* Order ID Section */}
              {orderIdEnabled && (
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">OrderId :</span>
                  <span className="text-sm font-bold text-slate-800">{orderId}</span>
                </div>
              )}

              {/* Vertical Divider (Only shows if both are enabled) */}
              {orderIdEnabled && billNoEnabled && <div className="h-4 w-[1px] bg-slate-300 mx-2" />}

              {/* Bill Number Section */}
              {billNoEnabled && (
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">Bill No. :</span>
                  <span className="text-sm font-bold text-slate-800">{billNumber != null ? formatNumber(billNumber) : "—"}</span>
                </div>
              )}
            </div>
          )}

          {/* Empty State for hidden references */}
          {!orderIdEnabled && !billNoEnabled && (
            <div className="flex items-center gap-2 py-2 px-3 bg-amber-50/50 rounded-xl text-amber-600 border border-amber-100/50">
              <Info className="w-4 h-4" />
              <span className="text-[10px] font-bold uppercase tracking-wider">References Hidden</span>
            </div>
          )}
        </div>
        {/* ✅ Financial Details - Using OrderPriceDetails style */}
        <div className="h-auto shadow-md rounded-lg bg-white -mt-1">
          <RowItem label="Sub total" value={`${formatAmount(Number(subtotal))} QAR`} />
          <RowItem label="Discount (-)" value={`${formatAmount(discount)} QAR`} />
          {deliveryFeeEnabled && deliveryFee > 0 && <RowItem label="Delivery Fee (+)" value={`${formatAmount(deliveryFee)} QAR`} />}
          <RowItem label="Total Amount" value={`${formatAmount(displayTotal)} QAR`} />
          {showLedgerDiscount && <RowItem label="Ledger discount (-)" value={`${formatAmount(ledgerDiscount)} QAR`} />}
          {showAdvanceAmount && <RowItem label="Advance Amount (-)" value={`${formatAmount(AdvanceAmount)} QAR`} />}
          <RowItem label="Received Amount" value={`${formatAmount(receivedAmount)} QAR`} />
          <RowItem label="Remaining balance" value={`${formatAmount(remainingAmount)} QAR`} />
          <RowItem label="Outstanding balance" value={`${formatAmount(outstandingAmount)} QAR`} />
          <RowItem label="Payment mode" value={<span className="capitalize">{paymentMethod}</span>} />
          <RowItem label="Order date & time" value={`${formatDate(orderDate)} · ${formatTime(orderTime)}`} />
        </div>
      </div>
    </div>
  );
};

export default OrderSummary;
